import { uniq } from 'lodash';
import { useMemo } from 'react';
import { ObjectType } from 'api/pushes';
import { getId } from 'components/organisms/projects/project-plan/phase-entity/utils';
import { PhaseEntityType, converToEntityId } from 'data/projects/phase-entity';
import { useSheetsFeature } from 'hooks/use-sheets-feature';
import { getFieldMapReducer } from 'routes/projects/list-v2/transformer';
import { floatUid } from 'utils/uid';
import { useGetAllProjectPhases } from './use-get-all-project-phases';
import { useGetAllProjectPhasesInTemplate } from './use-get-all-project-phases-in-template';
import { useGetAllSprints } from './use-get-all-sprints';
import { useGetAllTasks } from './use-get-all-tasks';
const frameTaskObject = (task, rootIndex)=>({
        key: `TASK-${task.taskId}`,
        id: task.taskId,
        title: task.taskName,
        startDay: task.startDay,
        duration: task.duration,
        assignee: task.assignee,
        private: task.private,
        data: task,
        rootIndex
    });
const frameSheetTabObject = (tab, rootIndex, taskId, parentTaskPrivate = false)=>({
        key: `SHEET-TAB-${taskId}_${tab.sheetTabId}`,
        id: `${taskId}_${tab.sheetTabId}`,
        title: tab.sheetTabName,
        startDay: 1,
        duration: 1,
        assignee: tab.assignee,
        // Cream row chrome: tab is private, or parent sheet task is private.
        private: Boolean(tab.private) || Boolean(parentTaskPrivate),
        data: tab,
        rootIndex,
        taskId
    });
const frameSprintObject = (sprint, rootIndex)=>({
        key: `SPRINT-${sprint.sprintId}`,
        id: sprint.sprintId,
        title: sprint.sprintName,
        startDay: sprint.startDay,
        duration: sprint.duration,
        private: sprint.private,
        data: sprint,
        rootIndex
    });
export const frameTaskObjectForBulkUpdate = (task, rootIndex, toFieldMap)=>{
    var _task_fields, _task_defaultTimeEntryBillable, _task_defaultTimeEntryBillable1;
    return {
        ...((_task_fields = task.fields) !== null && _task_fields !== void 0 ? _task_fields : []).reduce(toFieldMap, {}),
        ...task,
        key: `TASK-${task.taskId}`,
        id: task.taskId,
        title: task.taskName,
        startDay: task.startDay,
        duration: task.duration,
        assignee: task.assignee,
        private: task.private,
        data: {
            ...task,
            defaultTimeEntryBillable: (_task_defaultTimeEntryBillable = task.defaultTimeEntryBillable) !== null && _task_defaultTimeEntryBillable !== void 0 ? _task_defaultTimeEntryBillable : true
        },
        defaultTimeEntryBillable: (_task_defaultTimeEntryBillable1 = task.defaultTimeEntryBillable) !== null && _task_defaultTimeEntryBillable1 !== void 0 ? _task_defaultTimeEntryBillable1 : true,
        rootIndex
    };
};
const noop = ()=>({});
/**
 * First-seen wins across the whole template. Only claim tokens that resolve
 * in this phase — orphan tokens must not block a later phase's real task.
 */ const claimEntityOrder = (entityOrder, seen, entitiesMap)=>uniq(entityOrder !== null && entityOrder !== void 0 ? entityOrder : []).filter((id)=>{
        if (!entitiesMap[id] || seen.has(id)) {
            return false;
        }
        seen.add(id);
        return true;
    });
const getEntityFromRef = (entity, lookup)=>{
    if (typeof entity === 'number') {
        return lookup[entity];
    }
    return entity;
};
const getEntityIdFromRef = (entity, entityType)=>{
    if (typeof entity === 'number') {
        return converToEntityId(entity, entityType);
    }
    return getId(entity, true);
};
const taskToChild = (taskRef, { phaseIndex: index, allTasks, projectPhase })=>{
    var _task_subTasks;
    const task = getEntityFromRef(taskRef, allTasks);
    // `getEntityFromRef` can return undefined if a phase still references a
    // task ID that was deleted/renumbered (stale lookup). Without this guard,
    // `frameTaskObject(undefined)` accesses `task.taskId` and crashes the
    // template plan view with "Cannot read properties of undefined (reading
    // 'taskId')".
    if (!task) {
        return null;
    }
    return {
        ...frameTaskObject(task, index),
        type: 'TASK',
        level: 1,
        parentTotal: projectPhase.tasks.length,
        children: (_task_subTasks = task.subTasks) === null || _task_subTasks === void 0 ? void 0 : _task_subTasks.map((subTaskRef)=>{
            var _subTask_subTasks;
            const subTask = getEntityFromRef(subTaskRef, allTasks);
            if (!subTask) {
                return null;
            }
            return {
                ...frameTaskObject(subTask, index),
                type: 'TASK',
                level: 2,
                parentTotal: task.subTasks.length,
                children: (_subTask_subTasks = subTask.subTasks) === null || _subTask_subTasks === void 0 ? void 0 : _subTask_subTasks.map((_subTaskRef)=>{
                    const _subTask = getEntityFromRef(_subTaskRef, allTasks);
                    if (!_subTask) {
                        return null;
                    }
                    return {
                        ...frameTaskObject(_subTask, index),
                        parentTotal: subTask.subTasks.length,
                        level: 3,
                        type: 'TASK',
                        children: []
                    };
                }).filter(Boolean)
            };
        }).filter(Boolean)
    };
};
const taskToRow = (taskRef, { phaseIndex: index, allTasks, projectPhase, result, converter, toFieldMap, isSheetsEnabled = true })=>{
    var _task_sheetTemplate, _task_subTasks;
    const task = getEntityFromRef(taskRef, allTasks);
    // Same stale-lookup guard as `taskToChild` — when a phase references a
    // task ID that's no longer in `allTasks`, skip the row rather than
    // crashing on `task.taskId`.
    if (!task) {
        return;
    }
    if (!isSheetsEnabled && (task === null || task === void 0 ? void 0 : (_task_sheetTemplate = task.sheetTemplate) === null || _task_sheetTemplate === void 0 ? void 0 : _task_sheetTemplate.templateId)) {
        return;
    }
    let phaseTask = {};
    // Sheet Template Case
    if (task === null || task === void 0 ? void 0 : task.sheetTemplate) {
        var // Add Sheet tabs as child rows under the sheet task.
        _task_sheetTemplate_sheetTabs, _task_sheetTemplate1;
        // Reuse the same converter as normal tasks so the row keeps the
        // converter-specific shape (e.g. `frameTaskObjectForBulkUpdate`
        // spreads `...task` at the top level so AG Grid can read fields like
        // `effort` via `field: 'effort'`). Only the sheet-specific bits
        // (`SHEET-` key prefix and `taskId` accessor) need to be overlaid.
        phaseTask = {
            ...converter(task, index, toFieldMap),
            key: `SHEET-${task.taskId}`,
            taskId: task.taskId,
            type: 'TASK',
            level: 1,
            parentTotal: projectPhase.tasks.length,
            hierarchy: [
                projectPhase.projectPhaseId,
                task.taskId
            ].map(String)
        };
        task === null || task === void 0 ? void 0 : (_task_sheetTemplate1 = task.sheetTemplate) === null || _task_sheetTemplate1 === void 0 ? void 0 : (_task_sheetTemplate_sheetTabs = _task_sheetTemplate1.sheetTabs) === null || _task_sheetTemplate_sheetTabs === void 0 ? void 0 : _task_sheetTemplate_sheetTabs.forEach((tab)=>{
            var _task_sheetTemplate_sheetTabs, _task_sheetTemplate;
            result.push({
                ...frameSheetTabObject(tab, index, task.taskId, task.private),
                type: 'SHEET_TAB',
                level: 2,
                parentTotal: task === null || task === void 0 ? void 0 : (_task_sheetTemplate = task.sheetTemplate) === null || _task_sheetTemplate === void 0 ? void 0 : (_task_sheetTemplate_sheetTabs = _task_sheetTemplate.sheetTabs) === null || _task_sheetTemplate_sheetTabs === void 0 ? void 0 : _task_sheetTemplate_sheetTabs.length,
                hierarchy: [
                    projectPhase.projectPhaseId,
                    task.taskId,
                    `${task.taskId}_${tab.sheetTabId}`
                ].map(String)
            });
        });
    } else {
        phaseTask = {
            ...converter(task, index, toFieldMap),
            type: 'TASK',
            level: 1,
            parentTotal: projectPhase.tasks.length,
            hierarchy: [
                projectPhase.projectPhaseId,
                task.taskId
            ].map(String)
        };
    }
    result.push(phaseTask);
    (_task_subTasks = task.subTasks) === null || _task_subTasks === void 0 ? void 0 : _task_subTasks.forEach((subTaskRef)=>{
        var _subTask_subTasks;
        const subTask = getEntityFromRef(subTaskRef, allTasks);
        if (!subTask) {
            return;
        }
        const taskSubTask = {
            ...converter(subTask, index, toFieldMap),
            type: 'TASK',
            level: 2,
            parentTotal: task.subTasks.length,
            hierarchy: [
                projectPhase.projectPhaseId,
                task.taskId,
                subTask.taskId
            ].map(String)
        };
        result.push(taskSubTask);
        (_subTask_subTasks = subTask.subTasks) === null || _subTask_subTasks === void 0 ? void 0 : _subTask_subTasks.forEach((_subTaskRef)=>{
            const _subTask = getEntityFromRef(_subTaskRef, allTasks);
            if (!_subTask) {
                return;
            }
            const secondLevelSubTask = {
                ...converter(_subTask, index, toFieldMap),
                parentTotal: subTask.subTasks.length,
                level: 3,
                type: 'TASK',
                hierarchy: [
                    projectPhase.projectPhaseId,
                    task.taskId,
                    subTask.taskId,
                    _subTask.taskId
                ].map(String)
            };
            result.push(secondLevelSubTask);
        });
    });
};
const getPhaseSprint = ({ sprint, projectPhase, index })=>{
    var _projectPhase_sprints;
    return {
        ...frameSprintObject(sprint !== null && sprint !== void 0 ? sprint : {}, index),
        type: ObjectType.Sprint,
        level: 1,
        parentTotal: (_projectPhase_sprints = projectPhase.sprints) === null || _projectPhase_sprints === void 0 ? void 0 : _projectPhase_sprints.length,
        hierarchy: [
            projectPhase.projectPhaseId,
            sprint.sprintId
        ].map(String)
    };
};
const addSprintEntity = ({ projectPhase, allSprints, index, entitiesMap, isAgGrid })=>{
    var _projectPhase_sprints;
    (_projectPhase_sprints = projectPhase.sprints) === null || _projectPhase_sprints === void 0 ? void 0 : _projectPhase_sprints.forEach((sprintRef)=>{
        const key = getEntityIdFromRef(sprintRef, PhaseEntityType.Sprint);
        const sprint = getEntityFromRef(sprintRef, allSprints);
        const phaseSprint = getPhaseSprint({
            sprint: sprint !== null && sprint !== void 0 ? sprint : {},
            projectPhase,
            index
        });
        entitiesMap[key] = isAgGrid ? [
            phaseSprint
        ] : phaseSprint;
    });
};
export const useTransformTabularData = (phases)=>{
    const projectPhaseIds = useGetAllProjectPhasesInTemplate();
    const projectPhases = useGetAllProjectPhases();
    const allTasks = useGetAllTasks();
    const allSprints = useGetAllSprints();
    return useMemo(()=>{
        const _phases = phases !== undefined && phases !== null ? phases || [] : (projectPhaseIds !== null && projectPhaseIds !== void 0 ? projectPhaseIds : []).map((id)=>projectPhases[id]);
        const seenEntityIds = new Set();
        return _phases.map((projectPhase, index)=>{
            const entitiesMap = {};
            projectPhase.tasks.forEach((taskRef)=>{
                const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);
                if (!key) {
                    return;
                }
                entitiesMap[key] = taskToChild(taskRef, {
                    phaseIndex: index,
                    allTasks,
                    projectPhase
                });
            });
            addSprintEntity({
                projectPhase,
                allSprints,
                entitiesMap,
                index
            });
            const entities = claimEntityOrder(projectPhase.entityOrder, seenEntityIds, entitiesMap).map((id)=>entitiesMap[id]);
            return {
                key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
                id: projectPhase.projectPhaseId,
                title: projectPhase.projectPhaseName,
                startDay: projectPhase.startDay,
                duration: projectPhase.duration,
                type: 'PROJECT_PHASE',
                private: projectPhase.private,
                level: 0,
                data: projectPhase,
                rootIndex: index,
                parentTotal: 0,
                // Claimed values are task/sprint rows built above.
                children: entities
            };
        });
    }, [
        projectPhaseIds,
        projectPhases,
        phases,
        allTasks,
        allSprints
    ]);
};
export const useTransformTabularDataForAgGrid = (phases)=>{
    const projectPhaseIds = useGetAllProjectPhasesInTemplate();
    const projectPhases = useGetAllProjectPhases();
    const allTasks = useGetAllTasks();
    const allSprints = useGetAllSprints();
    const isSheetsEnabled = useSheetsFeature();
    return useMemo(()=>{
        var _phases_;
        const _phases = phases !== undefined && phases !== null ? phases || [] : (projectPhaseIds !== null && projectPhaseIds !== void 0 ? projectPhaseIds : []).map((id)=>projectPhases[id]);
        const result = [];
        const seenEntityIds = new Set();
        _phases.forEach((projectPhase, index)=>{
            const phase = {
                key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
                id: projectPhase.projectPhaseId,
                title: projectPhase.projectPhaseName,
                startDay: projectPhase.startDay,
                duration: projectPhase.duration,
                type: 'PROJECT_PHASE',
                private: projectPhase.private,
                level: 0,
                data: projectPhase,
                rootIndex: index,
                parentTotal: 0,
                hierarchy: [
                    projectPhase.projectPhaseId
                ].map(String)
            };
            result.push(phase);
            const entitiesMap = {};
            projectPhase.tasks.forEach((taskRef)=>{
                const taskEntity = [];
                const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);
                if (!key) {
                    return;
                }
                taskToRow(taskRef, {
                    phaseIndex: index,
                    allTasks,
                    projectPhase,
                    result: taskEntity,
                    converter: frameTaskObject,
                    toFieldMap: noop,
                    isSheetsEnabled
                });
                entitiesMap[key] = taskEntity;
            });
            addSprintEntity({
                projectPhase,
                allSprints,
                entitiesMap,
                index,
                isAgGrid: true
            });
            const entities = claimEntityOrder(projectPhase.entityOrder, seenEntityIds, entitiesMap).map((id)=>entitiesMap[id]);
            result.push(...entities.flat());
        });
        const newPhaseId = floatUid();
        const hasNewPhaseRow = _phases === null || _phases === void 0 ? void 0 : (_phases_ = _phases[(_phases === null || _phases === void 0 ? void 0 : _phases.length) - 1]) === null || _phases_ === void 0 ? void 0 : _phases_.isNew;
        if (hasNewPhaseRow) {
            return result;
        }
        result.push({
            key: `PROJECT_PHASE-${newPhaseId}`,
            id: newPhaseId,
            type: 'PROJECT_PHASE',
            level: 0,
            data: {
                isNew: true,
                entityOrder: []
            },
            rootIndex: _phases.length,
            hierarchy: [
                `${newPhaseId}`
            ],
            insertAt: null
        });
        return result;
    }, [
        projectPhaseIds,
        projectPhases,
        allTasks,
        phases,
        allSprints,
        isSheetsEnabled
    ]);
};
export const useTransformTabularDataForBulkTaskGrid = (lookup)=>{
    const projectPhaseIds = useGetAllProjectPhasesInTemplate();
    const projectPhases = useGetAllProjectPhases();
    const allTasks = useGetAllTasks();
    const allSprints = useGetAllSprints();
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    const toFieldMap = useMemo(()=>getFieldMapReducer(lookup), [
        lookup
    ]);
    const isSheetsEnabled = useSheetsFeature();
    return useMemo(()=>{
        var _phases_;
        const _phases = (projectPhaseIds !== null && projectPhaseIds !== void 0 ? projectPhaseIds : []).map((id)=>projectPhases[id]);
        const result = [];
        const seenEntityIds = new Set();
        _phases.forEach((projectPhase, index)=>{
            const phase = {
                key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
                id: projectPhase.projectPhaseId,
                title: projectPhase.projectPhaseName,
                startDay: projectPhase.startDay,
                duration: projectPhase.duration,
                type: 'PROJECT_PHASE',
                private: projectPhase.private,
                level: 0,
                data: projectPhase,
                rootIndex: index,
                parentTotal: 0,
                hierarchy: [
                    projectPhase.projectPhaseId
                ].map(String)
            };
            result.push(phase);
            const entitiesMap = {};
            projectPhase.tasks.forEach((taskRef)=>{
                const taskEntity = [];
                const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);
                if (!key) {
                    return;
                }
                taskToRow(taskRef, {
                    phaseIndex: index,
                    allTasks,
                    projectPhase,
                    result: taskEntity,
                    converter: frameTaskObjectForBulkUpdate,
                    toFieldMap,
                    isSheetsEnabled
                });
                entitiesMap[key] = taskEntity;
            });
            addSprintEntity({
                projectPhase,
                allSprints,
                entitiesMap,
                index,
                isAgGrid: true
            });
            const entities = claimEntityOrder(projectPhase.entityOrder, seenEntityIds, entitiesMap).map((id)=>entitiesMap[id]);
            result.push(...entities.flat());
        });
        const hasNewPhaseRow = _phases === null || _phases === void 0 ? void 0 : (_phases_ = _phases[(_phases === null || _phases === void 0 ? void 0 : _phases.length) - 1]) === null || _phases_ === void 0 ? void 0 : _phases_.isNew;
        if (hasNewPhaseRow) {
            return result;
        }
        const newPhaseId = floatUid();
        result.push({
            key: `PROJECT_PHASE-${newPhaseId}`,
            id: newPhaseId,
            type: 'PROJECT_PHASE',
            level: 0,
            data: {
                isNew: true,
                entityOrder: []
            },
            rootIndex: _phases.length,
            hierarchy: [
                `${newPhaseId}`
            ],
            insertAt: null
        });
        return result;
    }, [
        projectPhaseIds,
        projectPhases,
        allTasks,
        toFieldMap,
        allSprints,
        isSheetsEnabled
    ]);
};
